<template>
  <Spinner v-if="loading"></Spinner>
  <template v-else>
    <template v-if="isOneClick">
      <template v-if="isAtVehicleSelection">
        <VehicleSelection
          :change-vehicle-search="changeVehicleUserInput!"
          :policy-id="policyId"
          @abort="onAbort"
          @get-offer="onShowOffer"
          @scroll-to-top="scrollToTop"
        >
        </VehicleSelection>
      </template>

      <template v-if="isAtPriceInformation">
        <NewContractDetails
          :changeVehicleSearch="changeVehicleUserInput!"
          :policy-id="policyId"
          @abort="onAbort"
          @conclude="onConclude"
          @scroll-to-top="scrollToTop"
          @edit-vehicle="onEditVehicle"
        >
        </NewContractDetails>
      </template>

      <template v-if="isAtConclusion">
        <VehicleChangeCompleted
          :conclusion-number="motorConclusionResponse!.conclusionNumber"
          :evb-number="motorConclusionResponse!.evb"
        >
        </VehicleChangeCompleted>
      </template>
    </template>
    <CallCenterTile v-if="isCallCenter"></CallCenterTile>
  </template>
</template>

<script lang="ts" setup>
import { Spinner } from 'de-zurich-ui-library'
import { computed, onMounted, type Ref, ref } from 'vue'
import type { ChangeVehicleSearch } from '@/typesTenant/VehicleSelectionApi'
import {
  ChangeType,
  type MotorConclusionResponse,
  VehicleChangeState
} from '@/typesTenant/NewVehicleDetailApi'
import { policyIdReg } from '@/utilsTenant/PropsValidation'
import { fetchMotorVehicleChangeDataByPolicyId } from '@/stores/motorService'
import { navigate } from '@/utils/NavigationUtils'
import VehicleSelection from '@/componentsTenant/motor/vehiclechange/VehicleSelection.vue'
import NewContractDetails from '@/componentsTenant/motor/vehiclechange/newdetails/NewContractDetails.vue'
import CallCenterTile from '@/componentsTenant/motor/vehiclechange/CallCenterTile.vue'
import VehicleChangeCompleted from '@/componentsTenant/motor/vehiclechange/VehicleChangeCompleted.vue'
import { usePoliciesStore } from '@/stores/policiesStore'

const policiesStore = usePoliciesStore()

const props = defineProps({
  policyId: {
    type: String,
    required: true,
    validator(value: string): boolean {
      return policyIdReg.test(value)
    }
  }
})

const changeVehicleUserInput: Ref<ChangeVehicleSearch | null> = ref(null)
const motorConclusionResponse: Ref<MotorConclusionResponse | null> = ref(null)
const vehicleChangeState: Ref<VehicleChangeState> = ref(
  VehicleChangeState.VEHICLE_SELECTION
)
const loading = ref(true)
const changeType: Ref<ChangeType> = ref(ChangeType.CALL_CENTER)

function isInChangeType(value: string): boolean {
  return Object.values(ChangeType).includes(value as ChangeType)
}

onMounted(() => {
  fetchMotorVehicleChangeDataByPolicyId(props.policyId)
    .then((changeTypeDataResp) => {
      // worst case, no data => show call center tile
      if (!changeTypeDataResp || !isInChangeType(changeTypeDataResp.data))
        changeType.value = ChangeType.CALL_CENTER

      changeType.value = changeTypeDataResp.data as ChangeType

      // redirect to login page
      if (changeTypeDataResp.code === 401)
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
    })
    .catch((err) => {
      console.error('VehicleChangeContainer', err)
    })
    .finally(() => {
      loading.value = false
    })
})

const isAtVehicleSelection = computed(() => {
  return vehicleChangeState.value === VehicleChangeState.VEHICLE_SELECTION
})

const isAtPriceInformation = computed(() => {
  return vehicleChangeState.value === VehicleChangeState.PRICE_INFORMATION
})

const isAtConclusion = computed(() => {
  return vehicleChangeState.value === VehicleChangeState.CONCLUSION
})

const isOneClick = computed(() => {
  return changeType.value === ChangeType.ONE_CLICK
})

const isCallCenter = computed(() => {
  return changeType.value === ChangeType.CALL_CENTER
})

function onShowOffer(userInput: ChangeVehicleSearch): void {
  changeVehicleUserInput.value = userInput
  vehicleChangeState.value = VehicleChangeState.PRICE_INFORMATION
}

function onConclude(a_motorConclusionResponse: MotorConclusionResponse): void {
  motorConclusionResponse.value = a_motorConclusionResponse
  vehicleChangeState.value = VehicleChangeState.CONCLUSION
  policiesStore.setVehicleChangeThankYouValues({
    conclusionNumber: motorConclusionResponse.value.conclusionNumber,
    evbNumber: motorConclusionResponse.value.evb || ''
  })
  navigate('/danke?fahrzeug-wechseln')
}

function onAbort(): void {
  globalThis.history.back()
}

function onEditVehicle(): void {
  vehicleChangeState.value = VehicleChangeState.VEHICLE_SELECTION
}

function scrollToTop() {
  globalThis.scrollTo({ top: 0 })
}
</script>
